import { useTranslation } from '@thoughtspot/i18n';
import {
    Alert,
    BannerAlertType,
} from '@thoughtspot/radiant-react/widgets/alert';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { useValidateEmailLazyQuery } from '/@services/trial/trial-service';
import { Vertical } from '/@utils/layouts';
import { checkForWhitelistedEmail } from '../../account-activation-router/account-activation/common';
import { isEmail } from '../../email/email.util';
import {
    InviteButtonState,
    InviteUserContentV2ContainerProps,
} from '../invite-users-v2.interface';
import {
    getEmailError,
    getInviteButtonState,
    getUserChipFromEmail,
    setInviteButtonState,
} from '../invite-users-v2.util';
import { InviteUsersContentV2 } from './invite-users-content-v2';
import styles from './invite-users-content-v2.module.scss';

export const InviteUsersContentV2Container: React.FC<InviteUserContentV2ContainerProps> = ({
    isModalOpen,
    isLoading,
    onDismiss,
    onConfirm,
    emailChips,
    setEmailChips,
    sharables,
    comment,
    setComment,
    errorMsg,
    isAlertBannerVisible,
    setIsAlertBannerVisible,
    displayName,
    handleNameInputChange,
    isShareStepEnabled,
}) => {
    const { t } = useTranslation();
    const [searchInput, setSearchInput] = React.useState<string>('');
    const { validate, error, data } = useValidateEmailLazyQuery();

    const cleanUserInput = (input: string) => {
        return input?.split(',')[0]?.trim();
    };

    useEffect(() => {
        if (error || data) {
            const cleanedSearchInput = cleanUserInput(searchInput);
            const emailChipsList = [...emailChips];
            // Check if email already exists from api or if email format is invalid using regex
            if (error || !isEmail(cleanedSearchInput)) {
                emailChipsList.push(
                    getUserChipFromEmail(
                        {
                            emailId: cleanedSearchInput,
                            hasError: true,
                            errorString: getEmailError(
                                error,
                                !isEmail(cleanedSearchInput),
                            ),
                        },
                        emailChips.length,
                    ),
                );
            } else {
                emailChipsList.push(
                    getUserChipFromEmail(
                        {
                            emailId: cleanedSearchInput,
                        },
                        emailChips.length,
                    ),
                );
            }
            setSearchInput('');
            setEmailChips(emailChipsList);
            if (getInviteButtonState() === InviteButtonState.VALIDATE) {
                setInviteButtonState(InviteButtonState.CALL_NW);
            }
        }
    }, [error, data]);

    const onRemoveUser = (userId: string) => {
        const emails = [...emailChips];
        const filteredEmailChips = emails.filter(email => email.id !== userId);
        setEmailChips(filteredEmailChips);
    };

    const validateEmail = (emailId: string, inviteButtonClicked = false) => {
        if (isEmpty(emailId)) {
            setSearchInput('');
            return;
        }
        const trimmedEmailId = cleanUserInput(emailId);
        const emails = [...emailChips];
        const isEmailAlreadyPresent =
            emails.filter(email => email.content === trimmedEmailId)?.length >
            0;
        const isWhiteListedEmail = checkForWhitelistedEmail(trimmedEmailId);
        const containsEmailDomain = trimmedEmailId?.includes('@');
        if (
            isEmailAlreadyPresent ||
            (!isWhiteListedEmail && containsEmailDomain)
        ) {
            emails.push(
                getUserChipFromEmail(
                    {
                        emailId: trimmedEmailId,
                        hasError: true,
                        errorString: isEmailAlreadyPresent
                            ? t('invite.email.duplicateError')
                            : t('invite.email.whiteListError'),
                    },
                    emailChips.length,
                ),
            );
            // We want network call to happen in the next render
            if (inviteButtonClicked) {
                setInviteButtonState(InviteButtonState.CALL_NW);
            }
            setSearchInput('');
            setEmailChips(emails);
        } else {
            if (inviteButtonClicked) {
                setInviteButtonState(InviteButtonState.VALIDATE);
            }
            validate(trimmedEmailId);
        }
    };

    const confirmButtonText =
        !isShareStepEnabled || sharables?.length <= 0
            ? t('invite.modal.next.send')
            : t('NEXT');

    const onEnter = (event: React.KeyboardEvent<HTMLElement>) => {
        event?.stopPropagation();
        validateEmail(searchInput);
    };

    const onBackspace = () => {
        if (emailChips && emailChips?.length > 0) {
            onRemoveUser(emailChips[emailChips?.length - 1]?.id);
        }
    };

    const chipBodyClicked = (userId: string) => {
        // This function is not called when user clicks the close button on the chip
        // It will only edit the last added chip if clicked
        if (emailChips && emailChips?.length > 0 && isEmpty(searchInput)) {
            const lastChip = emailChips[emailChips?.length - 1];
            if (lastChip?.id === userId) {
                onRemoveUser(userId);
                setSearchInput(lastChip?.content);
            }
        }
    };

    const onConfirmClick = () => {
        // If there is some user input we want to validate that input if it is an email or not
        if (searchInput.length > 0) {
            validateEmail(searchInput, true);
        } else {
            onConfirm();
        }
    };

    const inviteButtonState = getInviteButtonState();

    useEffect(() => {
        if (inviteButtonState === InviteButtonState.CALL_NW) {
            onConfirm();
        }
    }, [onConfirm, inviteButtonState]);

    return (
        <Modal
            isOpen={isModalOpen}
            size={ModalSize.MEDIUM3}
            title={t('invite.modal.header')}
            confirmButton={{
                text: confirmButtonText,
                tooltip: t('homehub.invite_others.tooltip'),
                onConfirm: onConfirmClick,
                isLoading,
            }}
            dismissButton={{
                text: t('Cancel'),
            }}
            onDismiss={onDismiss}
            customFooterClassName={styles.customFooter}
            customHeaderClassName={styles.customHeader}
        >
            <Vertical grow shrink>
                <Alert.Banner
                    isVisible={isAlertBannerVisible}
                    message={errorMsg}
                    type={BannerAlertType.ERROR}
                    onClose={() => setIsAlertBannerVisible()}
                    className={styles.alertBanner}
                />
                <InviteUsersContentV2
                    emailChips={emailChips}
                    onRemoveUser={onRemoveUser}
                    comment={comment}
                    setComment={setComment}
                    onEnter={onEnter}
                    onBackspace={onBackspace}
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    displayName={displayName}
                    handleNameInputChange={handleNameInputChange}
                    validateEmail={validateEmail}
                    chipBodyClicked={chipBodyClicked}
                />
            </Vertical>
        </Modal>
    );
};
